/**
 * Country detection utilities
 */

export type CountryCode = string;

/**
 * Get country code from IP using external service (server-side)
 */
async function getCountryFromIP(ip: string): Promise<CountryCode> {
  const services: Array<{
    url: string;
    parser: (data: unknown) => string;
  }> = [
    {
      url: `https://api.country.is/${ip}`,
      parser: (data: unknown) => (data as { country: string }).country,
    },
    {
      url: `https://ipapi.co/${ip}/country_code/`,
      parser: (data: unknown) => (data as string).trim(),
    },
  ];

  for (const service of services) {
    try {
      const response = await fetch(service.url, {
        method: "GET",
        headers: {
          Accept: "application/json, text/plain",
        },
      });

      if (!response.ok) {
        continue;
      }

      let countryCode: string;
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        const data = await response.json();
        countryCode = service.parser(data);
      } else {
        const data = await response.text();
        countryCode = service.parser(data);
      }

      const trimmedCode = countryCode.trim().toUpperCase();

      if (
        trimmedCode &&
        trimmedCode.length === 2 &&
        /^[A-Z]{2}$/.test(trimmedCode)
      ) {
        return trimmedCode;
      }
    } catch (error) {
      console.warn(`Failed to get country from ${service.url}:`, error);
      continue;
    }
  }

  throw new Error("All IP geolocation services failed");
}

/**
 * Get country code from browser's locale (client-side fallback)
 */
export const getCountryFromLocale = (): CountryCode => {
  if (typeof window === "undefined") {
    return "US"; // Default for server-side
  }

  const locale = navigator.language || navigator.languages?.[0] || "en-US";
  const countryCode = locale.split("-")[1] || "US";
  return countryCode.toUpperCase();
};

/**
 * Server-side country detection using headers
 */
export const getCountryFromHeaders = async (
  headers: Headers
): Promise<CountryCode> => {
  try {
    // Extract IP from headers
    const forwarded = headers.get("x-forwarded-for");
    const realIP = headers.get("x-real-ip");
    const cfConnectingIP = headers.get("cf-connecting-ip");

    let clientIP: string | null = null;

    if (forwarded) {
      clientIP = forwarded.split(",")[0].trim();
    } else if (realIP) {
      clientIP = realIP;
    } else if (cfConnectingIP) {
      clientIP = cfConnectingIP;
    }

    console.log({ clientIP });

    // Try IP-based detection if we have a real IP
    if (clientIP && clientIP !== "127.0.0.1" && clientIP !== "::1") {
      try {
        return await getCountryFromIP(clientIP);
      } catch (error) {
        console.warn("IP geolocation failed:", error);
      }
    }

    // Fallback to Accept-Language header
    const acceptLanguage = headers.get("accept-language");
    if (acceptLanguage) {
      const languages = acceptLanguage.split(",");
      for (const lang of languages) {
        const locale = lang.split(";")[0].trim();
        if (locale.includes("-")) {
          const countryCode = locale.split("-")[1].toUpperCase();
          if (countryCode && countryCode.length === 2) {
            return countryCode;
          }
        }
      }
    }

    return "US";
  } catch (error) {
    console.warn("Country detection failed:", error);
    return "US";
  }
};

/**
 * Client-side country detection
 */
export const getCountryCode = async (): Promise<CountryCode> => {
  // Client-side: use fetch to call our API
  if (typeof window !== "undefined") {
    try {
      const response = await fetch("/api/country", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.countryCode || "US";
    } catch (error) {
      console.warn("Failed to get country from server:", error);
      return getCountryFromLocale();
    }
  }

  // Server-side fallback
  return "US";
};
